import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Ticket } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/categories', label: 'Categories' },
    { path: '/judging-process', label: 'Judging Process' },
    { path: '/winners', label: 'Winners' },
    { path: '/event', label: 'The Event' },
    { path: '/sponsors', label: 'Sponsors' },
    { path: '/about', label: 'About' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-gray-900/95 backdrop-blur-md shadow-lg' : 'bg-black/60 backdrop-blur-md'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <motion.div 
            className="flex items-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <NavLink to="/" className="flex items-center space-x-2 group">
              <img 
                src="https://titc.io/wp-content/uploads/2025/02/Logo-FA.png" 
                alt="SA FinTech Awards"
                className="h-8 transition-transform duration-300 group-hover:scale-105"
              />
            </NavLink>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `
                  px-4 py-2 rounded-md text-sm font-medium transition-all duration-300
                  ${isActive 
                    ? 'text-[#C1B3A3] bg-white/20' 
                    : 'text-white hover:text-[#C1B3A3] hover:bg-white/10'
                  }
                `}
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/tickets"
              className="ml-4 px-4 py-2 rounded-md text-sm font-medium text-white transition-all duration-300 flex items-center space-x-2 hover:scale-105"
              style={{ backgroundColor: '#C1B3A3' }}
            >
              <Ticket className="h-4 w-4" />
              <span>Reserve Your Ticket</span>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center space-x-4">
            <Link
              to="/tickets"
              className="px-4 py-2 rounded-md text-sm font-medium text-white transition-all duration-300 flex items-center space-x-2"
              style={{ backgroundColor: '#C1B3A3' }}
            >
              <Ticket className="h-4 w-4" />
              <span className="sr-only">Reserve Ticket</span>
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 rounded-md text-white hover:bg-white/20 transition-colors duration-300"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden overflow-hidden bg-gray-900/95 backdrop-blur-md border-t border-white/10"
          >
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `
                      block px-3 py-2 rounded-md text-base font-medium transition-all duration-300
                      ${isActive 
                        ? 'bg-white/20 text-[#C1B3A3]' 
                        : 'text-white hover:bg-white/10 hover:text-[#C1B3A3]'
                      }
                    `}
                  >
                    {item.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;